<template>
  <div v-if="lazyLoading" ref="commentElement" class="comment-section lazy-loading" />
  <div v-else ref="commentsRef" class="comment-section">
    <h5 v-if="showNumberOfComments" class="title">
      {{ t("comments") }} {{ `(${numberOfComments})` }}
    </h5>
    <div ref="commentItem" class="comment-container">
      <comment-list
        :comment-list-data="commentListData"
        :comments="displayComments"
        :academic-year="params.academicYear"
        :config="richTextEditorConfig"
        :from-discussion="params.fromDiscussion"
        :get-mentionable-student-list="getMentionableStudentList"
        :get-mentionable-teacher-list="getMentionableTeacherList"
        :viewed-user-id="viewedUserId"
        :is-anchor-to-last-comment="params.isAnchorToLastComment"
        :anchor="anchor"
        :allow-ask-salis="allowAskSalis"
        :can-ask-salis="canAskSalis"
        :is-ask-salis-supported-component="isAskSalisSupportedComponent"
        @previous-comments="getPreviousComments"
        @restrict-interaction="restrictInteraction"
        @mark-google-file="handleMarkGoogleFile"
        @mark-copy="handleMarkCopy"
        @anchor-to-last-comment="anchorToLastComment"
      />
    </div>
    <div ref="commentEditor" class="comment-section-footer">
      <div v-show="isAdding" class="comment-editor-container">
        <div v-if="!readOnly" class="comment-editor">
          <ellipsis-text class="name" :text="userFormattedName" />
          <rich-text-editor
            v-if="isAdding"
            ref="editor"
            :title="title"
            :kind="params.tinyMceType"
            :size="params.textSize"
            :initial-content="commentContent"
            :config="richTextEditorConfig"
            is-mandatory
            :disabled="freezeInteraction"
            :google-document-props="googleDocument"
            :ugc-document-prop="ugcDocument"
            :ugc-image-canvas-config-prop="ugcImageCanvasConfig"
            :enable-full-screen="isEnableFullScreen"
            @save-comment-set-edit-state="saveCommentAndSetEditState"
            @change="handleUpdateComment"
            @is-recording="handleRecordingStatusChanged"
            @ready="anchorToCommentEditor"
          />
          <cv-inline-notification
            v-if="hasError"
            :title="error.message"
            low-contrast
            kind="error"
            @close="handleCloseError"
          >
            <template #subtitle>
              <div v-safe-html="error.subMessage" />
            </template>
          </cv-inline-notification>
          <input-checkbox
            v-if="allowNotify"
            :id="'notify_' + dirtyId"
            class="notify-checkbox"
            @change="handleSelectNotify"
          >
            {{ notifyLabel || t("comment_notify_label") }}
          </input-checkbox>
          <div v-if="!freezeInteraction" class="actions">
            <cv-button
              class="cancel"
              kind="tertiary"
              size="small"
              :disabled="disableActions"
              @click="cancelComment"
            >
              {{ t("button_assignment_cancel") }}
            </cv-button>
            <fetch-data-button
              class="save"
              :kind="dataSaveButton.kind"
              :size="dataSaveButton.size"
              :loading="dataSaveButton.loading"
              :disabled="dataSaveButton.loading || disableActions"
              @click="saveComment"
            >
              {{ t("button_post") }}
            </fetch-data-button>
          </div>
        </div>
      </div>
      <div v-show="!isAdding" class="comment-section-nav">
        <cv-button-set v-if="(!freezeInteraction && hasNewComment) || allowAddComment">
          <cv-button
            v-show="hasNewComment"
            class="btn-show-new"
            kind="secondary"
            size="small"
            @click="showNewComments"
          >
            {{ showNewCommentLabel }}
            <svg-icon name="ArrowDown24" kind="action" />
          </cv-button>
          <cv-button
            v-if="allowAddComment"
            :class="addCommentButtonClass"
            size="small"
            :kind="params.commentButtonKind"
            :disabled="freezeInteraction || visibleByStudents"
            @click="handleAddComment"
          >
            <svg-icon :name="params.commentIcon" kind="action" />
            {{ params.addButtonLabel }}
          </cv-button>
        </cv-button-set>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import store from "./CommentStore";
import CommentApi from "@apis/comment.api";
import CommentList from "./CommentList.vue";
import ScrollUtils from "@utils/scroll.utils";
import { isDefined, isEmpty } from "@utils/var.check";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant";
import RandomUtils from "@/utils/random.utils";
import { COMMENT_POLLING_INTERVAL, COMMENT_RESOURCE_TYPE } from "@plugins/comment/comment.constant";
import { buildTeacherFeedbackAnchor } from "@modules/annotation/service/annotation.anchor.utils.ts";
import CommentEventBus from "@plugins/comment/service/event-bus";
import { TopicResourceEventBus } from "@plugins/topic/topic-resource.event.bus";
import InputCheckbox from "@plugins/common/components/InputCheckbox.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import EllipsisText from "@plugins/common/components/EllipsisText.vue";
import RichTextEditor from "@plugins/richtext/components/RichTextEditor.vue";
import { ref, computed, watch, onMounted, nextTick, onBeforeUnmount, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { useMention } from "@plugins/richtext/plugins/mentions/composables/mention.ts";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import { useStore } from "vuex";
import { useVm } from "@utils/vm.ts";
import { ApiResponse } from "@apis/api.ts";
import { CommentResponse } from "@plugins/comment/types.ts";
import RichTextContextMixin from "@modules/attempt/mixin/richtext.context.mixin";
import ugcApi from "@apis/ugc.api.ts";
import PollingUtils from "@utils/polling.utils.ts";
import {
  FileStatus,
  type FileStatusValues,
  TERMINAL_FILE_STATUSES,
} from "@plugins/file-uploader-v2/types.ts";
import { ContextMessage } from "@modules/conversation/types.ts";

defineOptions({
  mixins: [RichTextContextMixin],
});

export interface CommentPropsInterface {
  uuid?: string;
  editable?: boolean;
  deletable?: boolean;
  resourceUuid?: string;
  academicYear?: number;
  resourceId?: string | number;
  autoFocus?: boolean;
  allowNotify?: boolean;
  allowCreate?: boolean;
  readOnly?: boolean;
  canInitiate?: boolean;
  allowComment?: boolean;
  showComment?: boolean;
  freezeInteraction?: boolean;
  resourceType?: string;
  pageSize?: number;
  addButtonLabel?: string;
  tinyMceType?: string;
  loadPreviousLabel?: string;
  commentButtonKind?: string;
  textSize?: string;
  commentIcon?: string;
  noCommentLabel?: string;
  emptyCommentPlaceholder?: boolean;
  ownerRestricted?: boolean;
  numberOfComments?: number;
  isTeacherStudentView?: boolean;
  fromDiscussion?: boolean;
  teamId?: number;
  visibleByStudents?: boolean;
  isAnchorToLastComment?: boolean;
  isFeedback?: boolean;
}

// Props
interface Props {
  ugcImageCanvasConfigProp?: Record<string, unknown>;
  ugcDocumentProp?: Record<string, unknown>;
  googleDocumentProps?: Record<string, unknown>;
  params: CommentPropsInterface;
  title?: string;
  enableFullScreen?: boolean;
  getMentionableTeacherList?: () => object;
  getMentionableStudentList?: () => object;
  viewedUserId?: number;
  includeBuddyListMentions?: boolean;
  anchor?: string;
  allowAskSalis?: boolean;
  canAskSalis?: boolean;
  isAskSalisSupportedComponent?: boolean;
  notifyLabel?: string;
}

const {
  ugcImageCanvasConfigProp = {},
  ugcDocumentProp = {},
  googleDocumentProps = {},
  params,
  title = "",
  enableFullScreen = false,
  getMentionableTeacherList = () => ({}),
  getMentionableStudentList = () => ({}),
  viewedUserId = undefined,
  includeBuddyListMentions = false,
  anchor = "",
  allowAskSalis = false,
  canAskSalis = false,
  isAskSalisSupportedComponent = false,
  notifyLabel = "",
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  postCommentUpdated: [];
  editComment: [];
}>();

const vuexStore = useStore();
const { t } = useI18n();
const modal = useModal();
const { error, actionApiFailHandler, passiveApiFailHandler, hasError } = useErrorHandler();
const { mentionedTeachers, mentionedStudents, resetMention } = useMention();
const dirtyId = computed(() => {
  if (!params.resourceUuid) {
    return "";
  }
  return params.resourceUuid;
});
const { setDirty, promptPageDirty, promptComponentDirty, removeDirty } = useDirty(dirtyId);
const { trackWatch } = useEventBusWatch();
const { vmExpose } = useVm();

// Data
const notify = ref(false);
const lazyLoading = ref(true);
const hasNewComment = ref(false);
const readOnly = ref(params.readOnly);
const canInitiate = ref(params.canInitiate);
const allowNotify = ref(params.allowNotify);
const allowComment = ref(params.allowComment);
const showComment = ref(params.showComment);
const allowCreate = ref(params.allowCreate);
const autoFocus = ref(params.autoFocus);
const freezeInteraction = ref(params.freezeInteraction);
const commentContent = ref("");
const comments = ref<CommentResponse[]>([]);
const academicYear = ref(isDefined(params.academicYear) ? params.academicYear : 0);
const uploadFrom = ref("comment");
const commentListData = ref({
  editable: params.editable,
  deletable: params.deletable,
  resourceUuid: params.resourceUuid,
  resourceId: params.resourceId,
  resourceType: params.resourceType,
  freezeInteraction: params.freezeInteraction,
  allowNotify: params.allowNotify,
  allowComment: params.allowComment,
  tinyMceType: params.tinyMceType,
  loadPreviousLabel: params.loadPreviousLabel,
  isLoading: true,
  textSize: params.textSize,
  noCommentLabel: params.noCommentLabel,
  emptyCommentPlaceholder: params.emptyCommentPlaceholder,
  ownerRestricted: params.ownerRestricted,
  hasPrevious: false,
  isTeacherStudentView: params.isTeacherStudentView,
  teamId: params.teamId,
  isFeedback: params.isFeedback,
  notifyLabel: notifyLabel,
});
const googleDocument = ref({});
const ugcDocument = ref({});
const ugcImageCanvasConfig = ref({});
const pendingDocumentCallback = ref<(() => void) | null>(null);
const intervalTimer = ref(null);
const preloadedUuid = ref("");
const dataSaveButton = ref({
  kind: "primary",
  size: "small",
  loading: false,
});
const disableActions = ref(false);
const shouldAnchorToComment = ref(true);
const lazyLoadObserver = ref<IntersectionObserver | null>(null);

const UGC_UUID_REGEX =
  /<div class="sls-media"[^>]*data-uuid="([^"]+)"[^>]*media-type="AUDIO"[^>]*>/g;
const AUDIO_TAG_REGEX = /<div class="sls-media"[^>]*media-type="AUDIO"[^>]*>/g;

const pendingUgcUuids = ref<Set<string>>(new Set());
const inFlightAudioUploads = new Map<string, Promise<void>>();

// Template refs
const commentElement = useTemplateRef<HTMLElement>("commentElement");
const commentsRef = useTemplateRef<HTMLElement>("commentsRef");
const commentItem = useTemplateRef<HTMLElement>("commentItem");
const commentEditor = useTemplateRef<HTMLElement>("commentEditor");
const editor = useTemplateRef<HTMLElement>("editor");

// Computed
const userFormattedName = computed(() => vuexStore.getters[`${NAMESPACE.AUTH}/userFormattedName`]);
const lesson = computed(() => vuexStore.getters[`${NAMESPACE.RESOURCE}/getLesson`]);
const attemptContext = computed(() => vuexStore.state[NAMESPACE.ATTEMPT].attemptContext);

const numberOfComments = computed(() => {
  const numberOfComments = store.getNumberOfComments(params.resourceUuid);
  return isDefined(numberOfComments) ? numberOfComments : 0;
});

const isEnableFullScreen = computed(() => {
  return enableFullScreen && !freezeInteraction.value;
});

const isTeacherFeedbackCommentResourceType = computed(() => {
  return (
    params.resourceType === COMMENT_RESOURCE_TYPE.QUESTION_ANSWER ||
    params.resourceType === COMMENT_RESOURCE_TYPE.ATTEMPT_HISTORY ||
    params.resourceType === COMMENT_RESOURCE_TYPE.TEAM_ATTEMPT_HISTORY ||
    params.resourceType === COMMENT_RESOURCE_TYPE.QUESTION ||
    params.resourceType === COMMENT_RESOURCE_TYPE.ACTIVITY_QUIZ
  );
});

const showNumberOfComments = computed(() => {
  return !isTeacherFeedbackCommentResourceType.value;
});

const resource = computed(() => {
  // Used in attempt/richtext.context.mixin
  if (isTeacherFeedbackCommentResourceType.value) {
    return {
      id: lesson.value.id,
      uuid: lesson.value.uuid,
      title: lesson.value.title,
      resourceType: "LESSON",
      resourceDomain: lesson.value.resourceDomain,
    };
  }
  return {};
});

const isAdding = computed(() => {
  return store.getAddMode(params.resourceUuid);
});

const isEditing = computed(() => {
  return isAdding.value || isDefined(store.getEditCommentId(params.resourceUuid));
});

const allowAddComment = computed(() => {
  if (readOnly.value) return false;
  // student must wait teacher comment first before student can comment
  // for open ended question (not for forum, annotation, ITT)
  if (allowComment.value && !canInitiate.value) return comments.value.length > 0;

  return allowComment.value;
});

const richTextEditorConfig = computed(() => {
  return {
    fileLimit: {
      all: 10,
    },
    plugins: {
      fileUpload: {
        fileServiceConfig: {
          getContext: getContext,
          type: "ugc",
        },
      },
      drawing: {
        fileServiceConfig: {
          getContext: getContext,
          type: "ugc",
        },
      },
      google: {
        googleServiceConfig: {
          getContext: () => {
            return new Promise((resolve) => {
              resolve({
                resourceType: params.resourceType,
                resourceUuid: params.resourceUuid,
                resourceId: params.resourceId,
              });
            });
          },
          type: "google-attachment",
          googleAttachmentLimit: 10,
        },
      },
      recording: {
        fileServiceConfig: {
          getContext: getContext,
          type: "ugc",
        },
      },
      customMention: {
        mentionConfig: {
          getMentionableTeacherList: getMentionableTeacherList,
          getMentionableStudentList: getMentionableStudentList,
          includeBuddyListMentions: includeBuddyListMentions,
        },
      },
    },
  };
});

const visibleByStudents = computed(() => {
  return params?.visibleByStudents ?? false;
});

const showNewCommentLabel = computed(() => {
  return params.isFeedback ? t("show_new_feedback_label") : t("show_new_comment_label");
});

const addCommentButtonClass = computed(() => {
  if (params.resourceType === COMMENT_RESOURCE_TYPE.ANNOTATION) {
    return "btn-add-annotation-comment";
  } else {
    return "btn-add-comment";
  }
});

const displayComments = computed<CommentResponse[]>(() => {
  if (pendingUgcUuids.value.size === 0) return comments.value;
  return comments.value.map((comment) => {
    if (!comment.comment) return comment;
    const hasPending = [...pendingUgcUuids.value].some((uuid) =>
      comment.comment!.includes(`data-uuid="${uuid}"`),
    );
    if (!hasPending) return comment;
    return {
      ...comment,
      comment: comment.comment.replace(AUDIO_TAG_REGEX, t("comment_no_available_recording")),
    };
  });
});

// Methods
const anchorToCommentEditor = () => {
  // Execute pending document callback if rich text editor is now ready
  if (pendingDocumentCallback.value) {
    pendingDocumentCallback.value();
    pendingDocumentCallback.value = null;
  }

  const scrollContainer = commentEditor.value;
  if (isDefined(scrollContainer) && scrollContainer !== null) {
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
    ScrollUtils.scrollToView(scrollContainer, {
      behavior: "smooth",
      block: "end",
    });
  }
};

const extractUgcUuids = (comment: string): string[] => {
  return [...comment.matchAll(UGC_UUID_REGEX)].map((m) => m[1]);
};

const pollUgcFileReady = async (ugcUuid: string): Promise<void> => {
  const fetchMetaData = (uuid: string) => ugcApi.getMetaData({ uuid });

  const shouldContinuePolling = (response: { code: string; data: { fileStatus?: string } }) => {
    if (response.code !== "ok") return true;
    const status = response.data.fileStatus?.toUpperCase() ?? "";
    if (TERMINAL_FILE_STATUSES.has(status as FileStatusValues)) {
      throw new Error(`UGC file ${ugcUuid} terminal status: ${status}`);
    }
    return status !== FileStatus.READY;
  };

  await PollingUtils.pollingWithFibonacciInterval(
    ugcUuid,
    fetchMetaData,
    shouldContinuePolling,
    Date.now(),
    300000,
    3000,
    3000,
  );
};

const removeFromPending = (ugcUuid: string): void => {
  const next = new Set(pendingUgcUuids.value);
  next.delete(ugcUuid);
  pendingUgcUuids.value = next;
};

const handleAudioUploaded = async (ugcUuid: string): Promise<void> => {
  if (inFlightAudioUploads.has(ugcUuid)) return inFlightAudioUploads.get(ugcUuid);
  const flight = pollUgcFileReady(ugcUuid)
    .then(() => removeFromPending(ugcUuid))
    .catch(() => removeFromPending(ugcUuid))
    .finally(() => inFlightAudioUploads.delete(ugcUuid));
  inFlightAudioUploads.set(ugcUuid, flight);
  return flight;
};

const reconcileUgcStatus = (loadedComments: CommentResponse[]): void => {
  for (const comment of loadedComments) {
    if (!comment.comment) {
      // No content at all — show transcription placeholder directly
      comment.comment = t("comment_no_available_transcription");
      continue;
    }
    const ugcUuids = extractUgcUuids(comment.comment);
    if (ugcUuids.length === 0) continue;

    // Add all UUIDs to pending — displayComments will replace audio tags with placeholder
    const next = new Set(pendingUgcUuids.value);
    for (const ugcUuid of ugcUuids) {
      next.add(ugcUuid);
    }
    pendingUgcUuids.value = next;

    // Start polling for each UUID
    for (const ugcUuid of ugcUuids) {
      handleAudioUploaded(ugcUuid);
    }
  }
};

const getComments = (showNewComment) => {
  if (isEmpty(params.resourceUuid)) {
    commentListData.value.isLoading = false;
    lazyLoading.value = false;
    return;
  }
  const requestParams = {
    resourceUuid: params.resourceUuid,
    resourceType: params.resourceType,
    pageSize: params.pageSize,
    academicYear: academicYear.value,
    teamId: params.teamId,
  };

  comments.value = [];
  CommentApi.fetchComments(requestParams).then((response) => {
    //OK
    const handleError =
      response.code === "error" &&
      (!(params.resourceType === COMMENT_RESOURCE_TYPE.TEAM_ATTEMPT_HISTORY) ||
        !(response.errorMap[0] === "CommentResourceNotFound"));

    if (handleError) {
      return handleErrorEvent(response.errorMap, false);
    }
    const data = response.data;
    if (data && data.comments.length > 0) {
      comments.value = data.comments;
      reconcileUgcStatus(comments.value);
      commentListData.value.hasPrevious = data.hasNextPage;
      if (showNewComment) {
        if (shouldAnchorToComment.value) scrollToLatestComment(true);
        hasNewComment.value = false;
      } else if (params.autoFocus && shouldAnchorToComment.value) {
        scrollToLatestComment(false);
      }
    }
    if (lazyLoadObserver.value) {
      lazyLoadObserver.value.disconnect();
      lazyLoading.value = false;
      delete lazyLoadObserver.value;
    }
    commentListData.value.isLoading = false;

    if (!handleError) {
      store.fetchSummaryInfo(
        params.resourceUuid,
        params.resourceType,
        academicYear.value,
        params.teamId,
      );
    }

    //polling function to check for new comments
    pollComments();
  }, passiveApiFailHandler);
};

const getPreviousComments = () => {
  if (isEmpty(params.resourceUuid)) return;
  const earliestCommentId = comments.value[0].id;
  const requestParams = {
    resourceUuid: commentListData.value.resourceUuid,
    resourceType: commentListData.value.resourceType,
    cursor: earliestCommentId,
    pageSize: params.pageSize,
    academicYear: academicYear.value,
    teamId: params.teamId,
  };

  CommentApi.fetchComments(requestParams).then((response) => {
    //OK
    if (response.code === "error") {
      return handleErrorEvent(response.errorMap, false);
    }
    if (response.data && response.data.comments.length > 0) {
      const currentComments = comments.value;
      const previousComments = response.data.comments;
      reconcileUgcStatus(previousComments);
      comments.value = [...previousComments, ...currentComments];
      commentListData.value.hasPrevious = response.data.hasNextPage;
    }
  }, passiveApiFailHandler);
};

const handleAddComment = () => {
  const prompt =
    params.resourceType == COMMENT_RESOURCE_TYPE.ANNOTATION
      ? promptPageDirty
      : promptComponentDirty;
  prompt().then((response) => {
    if (!response) {
      return;
    }
    addComment();
  });
};

const addComment = () => {
  commentContent.value = "";
  preloadedUuid.value = RandomUtils.randomUuid();
  return showNewComments().then(() => {
    store.setAddMode(params.resourceUuid, true);
  });
};

const saveComment = () => {
  if (!editor.value?.validate()) return;
  dataSaveButton.value.loading = true;
  saveCommentApi();
};

const saveCommentAndSetEditState = () => {
  if (!editor.value?.validate()) return;
  dataSaveButton.value.loading = true;
  saveCommentApi().then((data) => {
    store.setEditComment(data.resourceUuid, data.id, commentContent.value);
  });
};

const saveCommentApi = () => {
  const requestData = {
    uuid: preloadedUuid.value,
    rt_comment: commentContent.value,
    notify: notify.value,
    resourceType: params.resourceType,
    resourceUuid: params.resourceUuid,
    academicYear: academicYear.value,
    attemptableUuid: params.fromDiscussion ? attemptContext.value?.attemptableUuid : null,
    attemptableType: params.fromDiscussion ? attemptContext.value?.attemptableType : null,
    attemptType: params.fromDiscussion ? attemptContext.value?.attemptType : null,
    teamId: params.teamId,
    mentionModel: {
      mentionedTeachers: mentionedTeachers.value,
      mentionedStudents: mentionedStudents.value,
      viewedUserId: viewedUserId,
    },
  };

  return new Promise((resolve, reject) => {
    CommentApi.createComment(requestData).then(
      (response) => {
        if (response.code === "error") {
          dataSaveButton.value.loading = false;
          return handleErrorEvent(response.errorMap, true);
        }
        if (response.data) {
          //check if more than one comment exists
          comments.value = isDefined(comments.value)
            ? [...comments.value, response.data]
            : [response.data];
          scrollToLatestComment(true);
          store.updateLastCommentInfo(params.resourceUuid, params.resourceType, response.data);
        }
        clearDocuments();
        resetMention();
        removeDirty(dirtyId.value);
        store.setAddMode(params.resourceUuid, false);
        store.fetchSummaryInfo(
          params.resourceUuid,
          params.resourceType,
          academicYear.value,
          params.teamId,
        );

        if (params.fromDiscussion) {
          TopicResourceEventBus.$emit("discussionAttempted", {});
        }

        dataSaveButton.value.loading = false;
        return resolve(response.data);
      },
      () => {
        dataSaveButton.value.loading = false;
        actionApiFailHandler();
        return reject();
      },
    );
  });
};

const cancelComment = () => {
  promptComponentDirty().then((response) => {
    if (!response) {
      return;
    }
    store.setAddMode(params.resourceUuid, false);
    commentContent.value = "";
    clearDocuments();
    resetMention();
    removeDirty(dirtyId.value);
    clearErrorState();
  });
};

const clearDocuments = () => {
  googleDocument.value = {};
  ugcDocument.value = {};
  ugcImageCanvasConfig.value = {};
};

const handleUpdateComment = (value) => {
  setDirty(dirtyId.value);
  commentContent.value = value;
};

const handleCloseError = () => {
  clearErrorState();
};

const clearErrorState = () => {
  error.value.message = "";
  error.value.subMessage = "";
};

const pollComments = () => {
  if (intervalTimer.value) clearInterval(intervalTimer.value);
  intervalTimer.value = setInterval(checkNewComments, COMMENT_POLLING_INTERVAL);
};

const checkNewComments = () => {
  const commentList = comments.value;
  const cursor =
    isDefined(commentList) && commentList.length > 0 ? commentList[commentList.length - 1].id : 0;
  const requestParams = {
    resourceUuid: params.resourceUuid,
    resourceType: params.resourceType,
    cursor: cursor,
    academicYear: academicYear.value,
    teamId: params.teamId,
  };

  CommentApi.checkNewComments(requestParams).then((response: ApiResponse<boolean>) => {
    //OK
    if (response.code === "error") {
      return handleErrorEvent(response.errorMap, false);
    }
    hasNewComment.value = response.data;
  }, passiveApiFailHandler);
};

const showNewComments = () => {
  return new Promise((resolve, reject) => {
    if (!isDefined(comments.value) || comments.value.length === 0) {
      getComments(true);
      return resolve();
    } else {
      const earliestComment = comments.value[0];
      const earliestCommentId = earliestComment.id;
      const requestParams = {
        resourceUuid: commentListData.value.resourceUuid,
        resourceType: commentListData.value.resourceType,
        cursor: earliestCommentId,
        academicYear: academicYear.value,
        teamId: params.teamId,
      };

      CommentApi.fetchNewComments(requestParams).then((response) => {
        //OK
        if (response.code === "error") {
          handleErrorEvent(response.errorMap, false);
          return reject();
        }
        if (response.data && response.data.comments.length > 0) {
          comments.value = response.data.comments;
          //hide show new comments button
          hasNewComment.value = false;
        }
        if (hasNewComment.value) {
          updateCommentTimeStamp();
        }
        store.fetchSummaryInfo(
          params.resourceUuid,
          params.resourceType,
          academicYear.value,
          params.teamId,
        );
        return resolve();
      }, passiveApiFailHandler);
    }
  });
};

const updateCommentTimeStamp = () => {
  store.setNewCommentTimeStamp(Date.now());
  scrollToLatestComment(true);
};

const scrollToLatestComment = (autoFocus) => {
  setTimeout(() => {
    //target modal container latest comment when clicking on view more from resource
    const commentDisplay = autoFocus
      ? commentsRef.value
      : window.document.getElementsByClassName("bx--modal-content")[0];
    if (isDefined(commentDisplay) && commentDisplay !== null) {
      const commentsToDisplay = commentDisplay.getElementsByClassName("comment-list-item");
      const lastComment = commentsToDisplay[commentsToDisplay.length - 1];
      const top =
        lastComment.getBoundingClientRect().top + window.document.scrollingElement?.scrollTop - 160;
      ScrollUtils.windowScrollTop(top);
    }
  }, 500);
};

const handleRecordingStatusChanged = (status) => {
  // disable save and cancel when recording
  disableActions.value = status;
  // scroll into view when recording finished
  if (!status) {
    nextTick(() => {
      const scrollContainer = commentEditor.value;
      if (isDefined(scrollContainer) && scrollContainer !== null) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
        ScrollUtils.scrollToTopWithHeaderOffset(
          scrollContainer,
          window.document.scrollingElement,
          60,
        );
      }
    });
  }
};

const handleErrorEvent = (errorMap, action) => {
  let content = "";
  const title = t("comment_title_label");
  let actionPrimary, actionSecondary, actionHideRequest;
  const error = errorMap[0];

  switch (error) {
    case "CommentResourceAccessDenied":
      content = t("comment_resource_access_denied_label");
      actionPrimary = restrictInteraction;
      actionSecondary = restrictInteraction;
      actionHideRequest = restrictInteraction;
      break;
    case "CommentResourceNotFound":
      content = t("comment_resource_not_found_label");
      actionPrimary = restrictInteraction;
      actionSecondary = restrictInteraction;
      actionHideRequest = restrictInteraction;
      break;
    case "CommentDeleted":
      content = t("comment_deleted_label");
      getComments(false);
      break;
    case "AssignmentIsLocked":
      showLockedErrorModal();
      return;
    default:
      if (action) {
        actionApiFailHandler();
      }
      return;
  }
  const modalParams = {
    title: title,
    content: content,
    primaryBtnLabel: t("button_ok"),
    secondaryBtnLabel: "CANCEL",
    actionPrimary: actionPrimary,
    actionSecondary: actionSecondary,
    actionHideRequest: actionHideRequest,
  };
  readOnly.value = true;
  modal.showMessage("warning", modalParams);
};

const showLockedErrorModal = () => {
  const props = {
    title: t("comment_error_assignment_locked_title"),
    content: t("comment_error_assignment_locked_content"),
    primaryBtnLabel: t("button_ok"),
    use_secondaryButton: false,
  };
  modal.showMessage("warning", props);
};

const restrictInteraction = () => {
  freezeInteraction.value = true;
};

const getContext = () => {
  // context here is for ugc creation
  return new Promise((resolve) => {
    resolve({
      resourceType: "COMMENT",
      resourceUuid: preloadedUuid.value, // new preloaded uuid, will keep renew when click on add new
      resourceId: 0, // new comment
    });
  });
};

const handleSelectNotify = (checked) => {
  notify.value = checked.length > 0 ? true : false;
};

const handleMarkGoogleFile = (data) => {
  if (isDefined(googleDocument) && googleDocument.value.id === data.id) return;
  if (!isAdding.value) {
    addComment().then(() => {
      nextTick(() => {
        pendingDocumentCallback.value = () => {
          googleDocument.value = data;
        };
      });
    });
  } else {
    googleDocument.value = data;
  }
};

const handleMarkCopy = (data) => {
  if (isDefined(ugcDocument) && ugcDocument.value.file === data.file) return;
  if (!isAdding.value) {
    addComment().then(() => {
      nextTick(() => {
        pendingDocumentCallback.value = () => {
          ugcDocument.value = data;
        };
      });
    });
  } else {
    ugcDocument.value = data;
  }
};

const handleMarkImageCopy = (data) => {
  if (isDefined(ugcImageCanvasConfig) && ugcImageCanvasConfig.value.imageUrl === data.imageUrl)
    return;
  if (!isAdding.value) {
    addComment().then(() => {
      nextTick(() => {
        pendingDocumentCallback.value = () => {
          ugcImageCanvasConfig.value = data;
        };
      });
    });
  } else {
    ugcImageCanvasConfig.value = data;
  }
};

const anchorToLastComment = () => {
  if (!shouldAnchorToComment.value) {
    return;
  }
  const scrollContainer = commentItem.value;
  if (isDefined(scrollContainer) && scrollContainer !== null) {
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
    ScrollUtils.scrollToView(scrollContainer, {
      behavior: "smooth",
      block: "end",
    });
  }
};

const handleAnchorToContextMessage = (contextMessage: ContextMessage) => {
  // turn of lazy loading & fetch comments to prepare to anchor
  if (!lazyLoading.value || isEmpty(contextMessage)) {
    return;
  }

  const targetAnchor = contextMessage.anchor.referenceId;
  const currentTeacherFeedbackAnchor = buildTeacherFeedbackAnchor(anchor);

  if (targetAnchor.includes(currentTeacherFeedbackAnchor)) {
    if (lazyLoadObserver.value) {
      lazyLoadObserver.value.disconnect();
      lazyLoadObserver.value = null;
    }
    lazyLoading.value = false;
    getComments(false);
  }
};

// Watch
watch(
  () => params,
  (newParams) => {
    if (!newParams) return;

    readOnly.value = newParams.readOnly;
    canInitiate.value = newParams.canInitiate;
    allowNotify.value = newParams.allowNotify;
    allowComment.value = newParams.allowComment;
    showComment.value = newParams.showComment;
    allowCreate.value = newParams.allowCreate;
    freezeInteraction.value = newParams.freezeInteraction;

    commentListData.value = {
      ...commentListData.value,
      editable: newParams.editable,
      deletable: newParams.deletable,
      resourceUuid: newParams.resourceUuid,
      resourceId: newParams.resourceId,
      resourceType: newParams.resourceType,
      freezeInteraction: newParams.freezeInteraction,
      allowNotify: newParams.allowNotify,
      allowComment: newParams.allowComment,
      tinyMceType: newParams.tinyMceType,
      loadPreviousLabel: newParams.loadPreviousLabel,
      textSize: newParams.textSize,
      noCommentLabel: newParams.noCommentLabel,
      emptyCommentPlaceholder: newParams.emptyCommentPlaceholder,
      ownerRestricted: newParams.ownerRestricted,
      isTeacherStudentView: newParams.isTeacherStudentView,
      teamId: newParams.teamId,
      isFeedback: newParams.isFeedback,
    };
  },
  {
    deep: true,
    immediate: true,
  },
);

watch(
  () => googleDocumentProps,
  (newValue) => {
    if (newValue && Object.keys(newValue).length > 0) {
      handleMarkGoogleFile(newValue);
    }
  },
  {
    deep: true,
    immediate: true,
  },
);

watch(
  () => ugcDocumentProp,
  (newValue) => {
    if (newValue && Object.keys(newValue).length > 0) {
      handleMarkCopy(newValue);
    }
  },
  {
    deep: true,
    immediate: true,
  },
);

watch(
  () => ugcImageCanvasConfigProp,
  (newValue) => {
    if (newValue && Object.keys(newValue).length > 0) {
      handleMarkImageCopy(newValue);
    }
  },
  {
    deep: true,
    immediate: true,
  },
);

watch(isEditing, (newValue, oldValue) => {
  if (newValue && !oldValue) {
    // check any comment adding/editing, emit to close other editing component
    emit("editComment");
  }
});

// Mounted
onMounted(() => {
  if (!showComment.value) {
    commentListData.value.isLoading = false;
    lazyLoading.value = false;
    return;
  }

  lazyLoadObserver.value = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        getComments(false);
      }
    });
  });

  if (commentElement.value) {
    lazyLoadObserver.value.observe(commentElement.value);
  }

  trackWatch(
    CommentEventBus.$on("commentDataRefetch", (anchorToComments) => {
      if (showComment.value) {
        shouldAnchorToComment.value = !!anchorToComments;
        showNewComments();
      }
    }),
  );

  trackWatch(CommentEventBus.$on("updateTimeStamp", updateCommentTimeStamp));

  if (allowAskSalis) {
    trackWatch(CommentEventBus.$on("anchorToContextMessage", handleAnchorToContextMessage));
  }
});

onBeforeUnmount(() => {
  removeDirty(dirtyId.value);
  resetMention();
  if (lazyLoadObserver.value) {
    lazyLoadObserver.value.disconnect();
    lazyLoading.value = false;
  }
  if (intervalTimer.value) {
    clearInterval(intervalTimer.value);
  }
  inFlightAudioUploads.clear();
  store.reset(params.resourceUuid);
});

vmExpose({
  autoFocus,
  uploadFrom,
  resource,
});
</script>

<style lang="scss">
@use "@styles/layout/breakpoint" as *;
@use "@styles/layout/z-map" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.comment-section {
  > .title {
    padding-bottom: $spacing-03;
    border-bottom: 1px solid $ui-03;
  }

  .btn-show-new {
    .sls-icon {
      margin: 0 0 0 $spacing-03;

      path {
        stroke: $icon-03;
      }
    }
  }

  .comment-editor-container {
    .comment-editor {
      padding: $spacing-05;
      margin: 0 (-$spacing-05);

      .name {
        @include carbon--type-style("card-title-01", true);
      }

      .rich-text-editor {
        margin: 0;
      }

      .actions {
        margin-top: $spacing-05;
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: $spacing-05;
      }

      .bx--btn {
        &.add {
          margin-left: auto;
        }
      }
    }
  }

  .comment-section-nav {
    > .bx--btn-set {
      display: flex;
      flex-flow: column;
      justify-content: center;
      align-items: center;
      gap: $spacing-03;

      > .bx--btn {
        max-width: unset;
        width: auto;
      }
    }
  }

  .content-subpage & {
    min-height: 100%;
    padding: $spacing-05;
    padding-bottom: $layout-05; // this is to create more space in subpage
    margin: 0 (-$spacing-05);
    background-color: $ui-01;

    .comment-section-nav {
      position: fixed;
      bottom: 0;
      left: 50%;
      transform: translate(-50%, 0);
      z-index: z-map("default");
      width: 100%;
      text-align: center;

      &:has(.btn-add-annotation-comment) {
        position: static;
        transform: initial;
      }

      > .bx--btn-set {
        > .bx--btn {
          position: absolute;
          bottom: 1rem;

          &.btn-add-comment {
            @include carbon--type-style("button-02");
            right: 1rem;
            display: flex;
            flex-flow: column;
            height: 3.75rem;
            width: 3.75rem;
            max-height: initial;
            min-width: initial;
            padding: $spacing-02;
            box-shadow: $shadow-03;

            .sls-icon {
              margin-right: 0;
            }
          }

          &.btn-add-annotation-comment {
            position: static;
            margin-top: $spacing-07;
          }

          &.btn-show-new {
            @include media("desktop") {
              left: 65%;
            }
          }
        }
      }
    }
  }
}
</style>
